import {
  ColumnId,
  ON_HAND_COLUMN_KEYS,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/constants';
import {
  formatLastOrderDisplay,
  LastPurchaseInfoMap,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/fulfilled-date-transformer';
import { OrderEntryPriceEditDisplayHeader } from '@/types';
import { OrderEntryTableRowData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';

export const LAST_ORDER_STAT_KEY = 'lastOrder';

/** One labelled value in the card's stat strip (already display-formatted). */
export interface OrderEntryMobileCardStat {
  key: string;
  label: string;
  value: string;
  /** Emphasis color for the value, resolved against the theme's text colors. */
  valueColor?: 'error';
}

export interface OrderEntryMobileCardStatsContext {
  areSubstitutionsEnabled: boolean;
  isGrossProfitHidden: boolean;
  labelsById: Record<string, string>;
  lastOrderLabel: string;
  lastPurchaseInfoMap: LastPurchaseInfoMap;
  priceEditDisplayHeadersByKey: Record<
    string,
    OrderEntryPriceEditDisplayHeader
  >;
  restaurantTimezone: string;
  selectedCollectionId: string;
  showConfidentialFields: boolean;
}

type StatValue = Pick<OrderEntryMobileCardStat, 'value' | 'valueColor'>;

const textStatValue = (value: string | null): StatValue | null =>
  value ? { value } : null;

const isOnHandField = (
  fieldId: string,
  context: OrderEntryMobileCardStatsContext,
): boolean =>
  context.areSubstitutionsEnabled && ON_HAND_COLUMN_KEYS.has(fieldId);

const buildOnHandStatValue = (
  item: OrderEntryTableRowData,
  display: string | null,
): StatValue => ({
  value:
    display ??
    (item.onHandQuantity === null ? '' : String(item.onHandQuantity)),
  valueColor: item.onHandQuantity === 0 ? 'error' : undefined,
});

const buildPriceEditStatValue = (
  item: OrderEntryTableRowData,
  header: OrderEntryPriceEditDisplayHeader,
  context: OrderEntryMobileCardStatsContext,
): StatValue | null => {
  if (header.is_confidential && !context.showConfidentialFields) {
    return null;
  }
  const display = item.priceEditDisplays[header.key]?.display ?? null;
  return isOnHandField(header.key, context)
    ? buildOnHandStatValue(item, display)
    : { value: display ?? '' };
};

const buildColumnStatValue = (
  item: OrderEntryTableRowData,
  fieldId: string,
  context: OrderEntryMobileCardStatsContext,
): StatValue | null => {
  if (isOnHandField(fieldId, context)) {
    return buildOnHandStatValue(item, null);
  }
  const showGrossProfit =
    !context.isGrossProfitHidden && context.showConfidentialFields;
  switch (fieldId) {
    case ColumnId.Unit:
      return { value: item.packDisplayString };
    case ColumnId.ItemCode:
      return { value: item.externalItemId };
    case ColumnId.Price:
      return { value: item.priceDisplayString };
    case ColumnId.GpPercentage:
      return showGrossProfit
        ? { value: item.grossProfitPercentageDisplayString }
        : null;
    case ColumnId.GpDollar:
      return showGrossProfit
        ? { value: item.grossProfitDollarDisplayString }
        : null;
    case ColumnId.Fulfilled:
      return {
        value: context.lastPurchaseInfoMap[item.variantPackUUID]?.display ?? '',
      };
    case ColumnId.Description:
      return textStatValue(item.description);
    case ColumnId.Brand:
      return textStatValue(item.brand);
    case ColumnId.PackSize:
      return textStatValue(item.packSize);
    case ColumnId.Notes:
      return textStatValue(
        item.requestNotes.find(
          (note) => note.collectionId === context.selectedCollectionId,
        )?.request ?? null,
      );
    default:
      return null;
  }
};

/** Card stats for the visible columns in the rep's saved order; empty text fields are skipped and last order is pinned last. */
export const buildOrderEntryMobileCardStats = (
  item: OrderEntryTableRowData,
  fieldIds: string[],
  context: OrderEntryMobileCardStatsContext,
): OrderEntryMobileCardStat[] => {
  const stats: OrderEntryMobileCardStat[] = [];
  for (const fieldId of fieldIds) {
    const header = context.priceEditDisplayHeadersByKey[fieldId];
    const statValue = header
      ? buildPriceEditStatValue(item, header, context)
      : buildColumnStatValue(item, fieldId, context);
    if (!statValue) {
      continue;
    }
    stats.push({
      key: fieldId,
      label: context.labelsById[fieldId] ?? fieldId,
      ...statValue,
    });
  }
  stats.push({
    key: LAST_ORDER_STAT_KEY,
    label: context.lastOrderLabel,
    value: formatLastOrderDisplay(
      context.lastPurchaseInfoMap[item.variantPackUUID],
      context.restaurantTimezone,
    ),
  });
  return stats;
};
